import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@/ui';

export default function ExitConfirmation({ when }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  // Listen for location changes and show prompt if needed
  useEffect(() => {
    if (!when) return;

    const handleBeforeUnload = (e) => {
      if (when) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);

  // Handle the actual navigation
  useEffect(() => {
    if (confirmedNavigation && pendingLocation) {
      navigate(pendingLocation.pathname + pendingLocation.search);
    }
  }, [confirmedNavigation, pendingLocation, navigate]);

  // Custom blocker implementation
  const blocker = useCallback(
    (nextLocation) => {
      if (when && nextLocation.pathname !== location.pathname && !confirmedNavigation) {
        setPendingLocation(nextLocation);
        setShowPrompt(true);
        return false;
      }
      return true;
    },
    [when, location, confirmedNavigation]
  );

  // Set up the blocker
  useEffect(() => {
    if (!when) return;

    // This is a workaround for React Router v6
    // which doesn't have a built-in blocking mechanism
    const unblock = navigate.block ? navigate.block(blocker) : () => {};

    return unblock;
  }, [when, navigate, blocker]);

  // When user clicks a link, this will catch it before React Router processes it
  useEffect(() => {
    if (!when) return;

    const handleClick = (e) => {
      // Find closest anchor tag
      const anchor = e.target.closest('a');
      if (!anchor || !anchor.href) return;

      // Skip interception for links marked as external
      if (anchor.dataset.externalLink === 'true') return;

      // Only handle internal links (same origin)
      const url = new URL(anchor.href);
      if (url.origin !== window.location.origin) return;

      // If it's an internal navigation, prevent default and show confirmation
      if (when && url.pathname !== location.pathname) {
        e.preventDefault();
        setPendingLocation({
          pathname: url.pathname,
          search: url.search,
        });
        setShowPrompt(true);
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [when, location]);

  const handleConfirm = () => {
    setShowPrompt(false);
    setConfirmedNavigation(true);
  };

  const handleCancel = () => {
    setShowPrompt(false);
    setPendingLocation(null);
  };

  return (
    <Modal isOpen={showPrompt} tone='info'>
      <ModalHeader>Exit Sequence Confirmation</ModalHeader>
      <ModalBody>Are you sure you want to exit a sequence without saving?</ModalBody>
      <ModalFooter>
        <Button onClick={handleConfirm} color='primary'>
          Confirm
        </Button>
        <Button color='secondary' onClick={handleCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}
